/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Collapse } from '@mui/material';
import ButtonMui from '@mui/material/Button';
import styled from 'styled-components';

import { expandLess, expandMore } from 'src/assets/images';
import { ITatchSpO2Data } from 'src/utils/bleCustomServices';
import { buf2hex, DeviceInfo } from 'src/utils/bleHelper';
import {
  connectToPatch, PatchManager, RecoverySession,
} from 'src/utils/patchManager';

// Tatch-Web Design System Colors
const tatchColors = {
  projectBackground: '#FAFAFA',
  white: '#ffffff',
  black: '#000000',
  darkGray: '#212121',
  gray: '#808080',
  lightGray: '#E5E5E5',
  concreteGray: '#F2F2F2',
  new: {
    blue: '#1A5CF5',
    darkBlue: '#0947D7',
    lightBlue: '#e1eaf9',
    gray: '#667070',
    borderGray: '#b2b2b2',
    lightGray: '#e8e8e8',
    text: {
      darkGray: '#5f5f5f',
      green: '#4caf50',
    },
    card: {
      border: '#E9EBEC',
    },
  },
  messages: {
    success: '#2F987A',
    warning: '#ff9800',
    error: '#fa6357',
  },
  status: {
    green: '#B3E5C8',
    red: '#F1AEA8',
  },
};

// Quick Test Constantss
const BATTERY_THRESHOLD_HIGH = 4.1;
const BATTERY_THRESHOLD_MEDIUM = 4.0;
const BATTERY_PCT_THRESHOLD_HIGH = 90;
const BATTERY_PCT_THRESHOLD_MEDIUM = 60;
const EXPECTED_DEVICE_STATE = 'connected';
const EXPECTED_ERROR = 'None';

type BatteryMode = 'pending' | 'percentage' | 'voltage';

const Container = styled.div`
  width: 90%;
  margin: 50px auto 0px;
  font-family: 'Akkurat', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const PatchCard = styled.div`
  margin-bottom: 30px;
  padding: 24px;
  background: ${tatchColors.white};
  border: 1px solid ${tatchColors.new.card.border};
  border-radius: 20px;
  box-shadow: none;
`;

const SummaryTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 12px;
  font-family: 'Montserrat-SemiBold', sans-serif;
  font-size: 20px;
  color: ${tatchColors.darkGray};
  font-weight: 600;
`;

const QuickTestBadge = styled.div<{ $isPass: boolean }>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 13.2px 26.4px;
  margin-bottom: 20px;
  font-family: 'Montserrat-SemiBold', sans-serif;
  font-size: 30.8px;
  font-weight: 600;
  color: ${tatchColors.white};
  background-color: ${props => props.$isPass ? tatchColors.messages.success : tatchColors.messages.error};
  border-radius: 0;
  text-transform: capitalize;
  letter-spacing: 1.54px;
`;

const QuickTestStatusBadge = styled.div`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 13.2px 26.4px;
  margin-bottom: 20px;
  font-family: 'Montserrat-SemiBold', sans-serif;
  font-size: 30.8px;
  font-weight: 600;
  color: ${tatchColors.black};
  background-color: ${tatchColors.concreteGray};
  border-radius: 0;
  letter-spacing: 1.54px;
`;

const SummaryTable = styled.table`
  border-collapse: collapse;
  width: 800px;
  font-family: 'Akkurat', sans-serif;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${tatchColors.new.lightGray};
`;

const TableCell = styled.td<{ $isLabel?: boolean }>`
  padding: ${props => props.$isLabel ? '12px 2px 12px 0px' : '12px 8px 12px 2px'};
  text-align: left;
  font-weight: ${props => props.$isLabel ? '600' : '400'};
  color: ${props => props.$isLabel ? tatchColors.darkGray : tatchColors.black};
  font-size: 16px;
`;

const RecycleButton = styled.button`
  padding: 19.8px 39.6px;
  font-size: 22px;
  font-weight: 600;
  font-family: 'Montserrat-SemiBold', sans-serif;
  letter-spacing: 1.54px;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 12px;
  box-shadow: none;
  background-color: ${tatchColors.new.blue};
  color: ${tatchColors.white};
  border: 1px solid ${tatchColors.new.blue};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${tatchColors.new.darkBlue};
    border: 1px solid ${tatchColors.new.darkBlue};
  }

  &:active {
    background-color: ${tatchColors.new.darkBlue};
  }
`;

const ConnectButton = styled.button`
  padding: 14px 28px;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Montserrat-SemiBold', sans-serif;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 12px;
  box-shadow: none;
  background-color: ${tatchColors.white};
  color: ${tatchColors.new.blue};
  border: 2px solid ${tatchColors.new.blue};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${tatchColors.new.lightBlue};
    border: 2px solid ${tatchColors.new.blue};
  }

  &:active:not(:disabled) {
    background-color: ${tatchColors.new.lightBlue};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ExpandButton = styled(ButtonMui)`
  font-family: 'Montserrat-SemiBold', sans-serif;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  border-radius: 12px;
  box-shadow: none;
  border: 1px solid ${tatchColors.new.borderGray};
  color: ${tatchColors.new.blue};
  background: ${tatchColors.white};
  
  &:hover {
    background: ${tatchColors.new.lightBlue};
    border: 1px solid ${tatchColors.new.blue};
  }
`;

const AdvancedSection = styled.div`
  padding: 24px;
  background-color: ${tatchColors.concreteGray};
  border-radius: 12px;
`;

const SectionTitle = styled.h4`
  font-family: 'Montserrat-SemiBold', sans-serif;
  font-size: 16px;
  color: ${tatchColors.darkGray};
  margin-top: 0;
  margin-bottom: 16px;
  font-weight: 600;
`;

const ControlButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Akkurat', sans-serif;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 12px;
  box-shadow: none;
  background-color: ${tatchColors.white};
  color: ${tatchColors.new.blue};
  border: 1px solid ${tatchColors.new.borderGray};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${tatchColors.new.lightBlue};
    border: 1px solid ${tatchColors.new.blue};
  }
`;

interface IProps {
  [key: string]: any;
}

interface IState {
  [key: string]: any;
}

interface ITatchMonState {
  devices: {[id: string]: PatchManager}
}

interface IPatchInterfaceProps {
  key: string;
  manager: PatchManager;
  unmountHandler: (id: string) => void;
}

/**
 * Component for the interface to a single patch.
 *
 * @class      PatchInterface (name)
 */
class PatchInterface extends React.Component<IPatchInterfaceProps, IState> {
  constructor(props: IPatchInterfaceProps) {
    super(props);
    this.state = {
      name: this.props.manager.name,
      status: 'Idle',
      deviceState: 'loading...',
      deviceStateValue: null,
      batteryVoltage: null,
      batterySoc: null,
      batteryMode: 'pending' as BatteryMode,
      isSamplingBattery: false,
      batteryTestComplete: false,
      isRecycling: false,
      error: 'None',
      sessionIsActive: false,
      sessionStartTime: null,
      spo2Data: null,
      commandResponse: 'None',
      deviceInfo: null,
      isAdvancedExpanded: false,
    };

    this.onDisconnectPatch = this.onDisconnectPatch.bind(this);
    this.onPowerOffPatch = this.onPowerOffPatch.bind(this);
    this.onResetPatch = this.onResetPatch.bind(this);
    this.onStartSession = this.onStartSession.bind(this);
    this.onStopSession = this.onStopSession.bind(this);
    this.onGetPatchState = this.onGetPatchState.bind(this);
    this.onGetError = this.onGetError.bind(this);
    this.onFormatStorage = this.onFormatStorage.bind(this);
    this.onSpamCommand = this.onSpamCommand.bind(this);
    this.onRecoverCatchUp = this.onRecoverCatchUp.bind(this);
    this.onCommandSend = this.onCommandSend.bind(this);
    this.onToggleAdvanced = this.onToggleAdvanced.bind(this);
    this.onRecycle = this.onRecycle.bind(this);
    this.onTestBattery = this.onTestBattery.bind(this);
    this.completeVoltageBatteryTest = this.completeVoltageBatteryTest.bind(this);
  }

  componentDidMount() {
    Promise.all([
      this.props.manager.getDeviceState().catch(() => null),
      this.props.manager.getDeviceInfo().catch(() => null),
    ])
      .then(([stateInfo, deviceInfo]) => {
        if (!stateInfo && !deviceInfo) {
          this.setStatus('Connection setup failed: could not read device state or device information.');

          return;
        }

        const stateUpdate: Partial<IState> = {};

        if (deviceInfo) {
          stateUpdate.deviceInfo = deviceInfo;
        }

        if (stateInfo) {
          let str = '';
          if (stateInfo.success) {
            const batteryVoltage = stateInfo.batteryVoltage;
            str += `${stateInfo.stateString} (${stateInfo.state}).`;
            str += ` ${stateInfo.remainingPages} pages unsent. Battery voltage: ${batteryVoltage != null ? batteryVoltage / 1000 : 0}V.`;
            stateUpdate.deviceStateValue = stateInfo.stateString?.toLowerCase() || null;
            stateUpdate.batteryVoltage = batteryVoltage != null ? batteryVoltage / 1000 : null;
          }
          else {
            str += 'GetDeviceState unsuccessful.';
            stateUpdate.deviceStateValue = null;
            stateUpdate.batteryVoltage = null;
          }
          str += ` rawMsg: (${stateInfo.rawMsg})`;
          stateUpdate.deviceState = str;
        }

        this.setState(stateUpdate, () => {
          const failures: string[] = [];
          if (!stateInfo) {
            failures.push('device state');
          }
          if (!deviceInfo) {
            failures.push('device information');
          }

          if (failures.length > 0) {
            this.setStatus(`Connection setup failed: could not read ${failures.join(' or ')}.`);
          }

          if (!deviceInfo || this.isBatteryTestBlocked()) {
            return;
          }

          this.onTestBattery();
        });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'unexpected error';
        this.setStatus(`Connection setup failed: ${message}.`);
        console.error('Connect init failed:', err);
      });
  }

  getDisplayModelNumber(deviceInfo: DeviceInfo | null): string {
    if (!deviceInfo?.modelNumber) {
      return 'Loading...';
    }

    // Check if the string contains the replacement character (U+FFFD) or other invalid characters
    const hasInvalidChars = /[\uFFFD\u0000-\u001F\u007F-\u009F]/.test(deviceInfo.modelNumber);
    
    if (hasInvalidChars && deviceInfo.modelNumberBinary) {
      // If invalid characters found, show hex representation
      return `0x${deviceInfo.modelNumberBinary.replace(/\s+/g, '')}`;
    }

    // Filter out any remaining invalid characters and return clean string
    return deviceInfo.modelNumber.replace(/[\uFFFD\u0000-\u001F\u007F-\u009F]/g, '') || 'N/A';
  }

  getPatchVersion(deviceInfo: DeviceInfo | null): string {
    if (!deviceInfo) {
      return 'Loading...';
    }

    if (!deviceInfo.hardwareRevision) {
      return 'Not detected';
    }

    const major = parseInt(deviceInfo.hardwareRevision.split('.')[0], 10);

    if (Number.isNaN(major)) {
      return 'Not detected';
    }

    return major >= 5 ? 'Patch 1.5' : 'Patch 1.0';
  }

  getBattRating(): 'high' | 'medium' | 'low' | null {
    if (this.state.batteryMode === 'percentage') {
      if (this.state.batterySoc === null) {
        return null;
      }
      if (this.state.batterySoc > BATTERY_PCT_THRESHOLD_HIGH) {
        return 'high';
      }
      if (this.state.batterySoc >= BATTERY_PCT_THRESHOLD_MEDIUM) {
        return 'medium';
      }

      return 'low';
    }

    if (this.state.batteryMode === 'voltage') {
      if (this.state.batteryVoltage === null) {
        return null;
      }
      if (this.state.batteryVoltage > BATTERY_THRESHOLD_HIGH) {
        return 'high';
      }
      if (this.state.batteryVoltage >= BATTERY_THRESHOLD_MEDIUM) {
        return 'medium';
      }

      return 'low';
    }

    return null;
  }

  getBatteryLevelDisplay(): string {
    if (this.state.batteryVoltage === null) {
      return 'N/A';
    }

    if (this.state.batteryMode === 'percentage' && this.state.batterySoc !== null) {
      const rating = this.getBattRating();
      const ratingLabel = rating
        ? rating.charAt(0).toUpperCase() + rating.slice(1)
        : 'N/A';

      return `${ratingLabel} (${this.state.batterySoc} %) · ${this.state.batteryVoltage.toFixed(2)} V`;
    }

    if (this.state.batteryMode === 'voltage' && this.state.batteryTestComplete) {
      const rating = this.getBattRating();
      if (rating === null) {
        return 'N/A';
      }
      const ratingLabel = rating.charAt(0).toUpperCase() + rating.slice(1);

      return `${ratingLabel} (${this.state.batteryVoltage.toFixed(2)} [V])`;
    }

    return `${this.state.batteryVoltage.toFixed(2)} [V]`;
  }

  getBatteryLevelColor(): string {
    const rating = this.getBattRating();
    if (rating === 'high') return tatchColors.messages.success;
    if (rating === 'medium') return tatchColors.messages.warning;
    if (rating === 'low') return tatchColors.messages.error;

    return 'inherit';
  }

  isBatteryTestBlocked(): boolean {
    return this.state.isSamplingBattery
      || this.state.sessionIsActive
      || this.state.isRecycling
      || !!this.props.manager.session
      || this.getPatchVersion(this.state.deviceInfo) === 'Loading...';
  }

  completeVoltageBatteryTest() {
    const finish = (batteryVoltage: number | null) => {
      if (batteryVoltage === null) {
        this.setState({
          isSamplingBattery: false,
          batteryMode: 'pending',
          batteryTestComplete: false,
        });
        this.setStatus('Battery test failed: could not read voltage.');

        return;
      }

      this.setState({
        batteryMode: 'voltage',
        batterySoc: null,
        batteryVoltage,
        isSamplingBattery: false,
        batteryTestComplete: true,
      });
      this.setStatus('Battery test complete (voltage).');
    };

    if (this.state.batteryVoltage !== null) {
      finish(this.state.batteryVoltage);

      return;
    }

    this.setStatus('Reading battery voltage...');
    this.props.manager.getDeviceState()
      .then((info) => {
        if (info.success && info.batteryVoltage != null) {
          finish(info.batteryVoltage / 1000);
        }
        else {
          finish(null);
        }
      })
      .catch(() => {
        finish(null);
      });
  }

  runSocBatteryTest() {
    this.setState({
      isSamplingBattery: true,
      batteryTestComplete: false,
      batteryMode: 'pending',
      batterySoc: null,
    });
    this.setStatus('Sampling battery...');

    this.props.manager.sampleBattery()
      .then((reading) => {
        this.setState({
          batteryMode: 'percentage',
          batterySoc: reading.soc,
          batteryVoltage: reading.voltageMv / 1000,
          isSamplingBattery: false,
          batteryTestComplete: true,
        });
        this.setStatus('Battery test complete (SOC).');
      })
      .catch((err: Error) => {
        console.warn('Battery SOC sample failed:', err.message);
        if (err.message === 'Battery sample timed out') {
          this.completeVoltageBatteryTest();
        }
        else {
          this.setState({
            isSamplingBattery: false,
            batteryMode: 'pending',
            batteryTestComplete: false,
          });
          this.setStatus(`Battery test failed: ${err.message}`);
        }
      });
  }

  renderQuickTestBadge() {
    if (this.state.deviceStateValue === null || this.state.batteryVoltage === null) {
      return (
        <QuickTestStatusBadge>Loading...</QuickTestStatusBadge>
      );
    }

    if (this.state.isSamplingBattery) {
      return (
        <QuickTestStatusBadge>Sampling battery...</QuickTestStatusBadge>
      );
    }

    if (!this.state.batteryTestComplete) {
      return null;
    }

    return (
      <QuickTestBadge $isPass={this.isPass()}>
        {this.isPass() ? 'Pass' : 'Fail'}
      </QuickTestBadge>
    );
  }

  isPass(): boolean {
    // Fail if battery is low
    if (this.getBattRating() === 'low') {
      return false;
    }
    // Fail if device state is not connected
    if (this.state.deviceStateValue?.toLowerCase() !== EXPECTED_DEVICE_STATE) {
      return false;
    }
    // Fail if error is not None
    if (this.state.error !== EXPECTED_ERROR) {
      return false;
    }

    // Otherwise pass
    return true;
  }

  render() {
    const dataDisplay = (this.props.manager._spo2_service) ?
      <LiveSpO2View data={this.state.spo2Data}></LiveSpO2View> :
      <div>Live HR/SpO2 not available</div>;

    const deviceInfo: DeviceInfo | null = this.state.deviceInfo;

    return (
      <PatchCard>
        {/* Summary Section */}
        <div style={{ marginBottom: '24px' }}>
          {this.renderQuickTestBadge()}
          <SummaryTable>
            <tbody>
              <TableRow>
                <TableCell $isLabel>Patch Version:</TableCell>
                <TableCell>{this.getPatchVersion(deviceInfo)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell $isLabel>Hardware Version:</TableCell>
                <TableCell>{deviceInfo?.hardwareRevision || 'Loading...'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell $isLabel>Firmware Version:</TableCell>
                <TableCell>{deviceInfo?.firmwareRevision || 'Loading...'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell $isLabel>Model Number:</TableCell>
                <TableCell>{this.getDisplayModelNumber(deviceInfo)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell $isLabel>Device State:</TableCell>
                <TableCell
                  style={{
                    color: this.state.deviceStateValue?.toLowerCase() === EXPECTED_DEVICE_STATE
                      ? tatchColors.messages.success
                      : tatchColors.messages.error,
                  }}
                >
                  {this.state.deviceStateValue
                    ? this.state.deviceStateValue.toUpperCase()
                    : 'N/A'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell $isLabel>Battery Level:</TableCell>
                <TableCell
                  style={{
                    color: this.getBatteryLevelColor(),
                  }}
                >
                  {this.getBatteryLevelDisplay()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell $isLabel>Error:</TableCell>
                <TableCell
                  style={{
                    color: this.state.error === EXPECTED_ERROR
                      ? tatchColors.messages.success
                      : tatchColors.messages.error,
                  }}
                >
                  {this.state.error}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} style={{ textAlign: 'left', padding: '16px 8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}
                  >
                    <RecycleButton onClick={this.onRecycle}>
                      Recycle
                    </RecycleButton>
                    <div style={{ color: tatchColors.black, fontSize: '14px' }}>
                      <span style={{ fontWeight: '600', fontFamily: "'Montserrat-SemiBold', sans-serif" }}>Status: </span>
                      {this.state.status}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </tbody>
          </SummaryTable>
        </div>

        {/* Advanced Section */}
        <div>
          <ExpandButton
            onClick={this.onToggleAdvanced}
            startIcon={this.state.isAdvancedExpanded ? <img width="20" src={expandLess} alt="collapse" /> : <img width="20" src={expandMore} alt="expand" />}
            variant="outlined"
            style={{ marginBottom: '12px' }}
          >
            {this.state.isAdvancedExpanded ? 'Hide Advanced' : 'Show Advanced'}
          </ExpandButton>
          <Collapse in={this.state.isAdvancedExpanded} timeout="auto" unmountOnExit>
            <AdvancedSection>
              {/* Full Device Info */}
              <div style={{ marginBottom: '24px' }}>
                <SectionTitle>Full Device Information</SectionTitle>
                <DeviceInfoView patchManager={this.props.manager}/>
              </div>

              {/* Control Buttons */}
              <div style={{ marginBottom: '24px' }}>
                <SectionTitle>Controls</SectionTitle>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  <ControlButton onClick={this.onDisconnectPatch}>Disconnect from Patch</ControlButton>
                  <ControlButton onClick={this.onPowerOffPatch}>Power Off & Disconnect</ControlButton>
                  <ControlButton onClick={this.onResetPatch}>Reset</ControlButton>
                  <ControlButton onClick={this.onStartSession}>Start Session</ControlButton>
                  <ControlButton onClick={this.onStopSession}>Stop Session</ControlButton>
                  <ControlButton onClick={this.onGetPatchState}>Refresh Device State</ControlButton>
                  <ControlButton onClick={this.onGetError}>Get Error</ControlButton>
                  <ControlButton onClick={this.onFormatStorage}>Format Storage</ControlButton>
                  <ControlButton onClick={this.onSpamCommand}>Spam Commands</ControlButton>
                  <ControlButton onClick={this.onRecoverCatchUp}>Recover from Crash</ControlButton>
                </div>
              </div>

              {/* Status Information */}
              <div style={{ marginBottom: '24px' }}>
                <SectionTitle>Status</SectionTitle>
                <div style={{ color: tatchColors.black, fontSize: '14px', marginBottom: '8px' }}>
                  Status message: {this.state.status}
                </div>
                <div style={{ color: tatchColors.black, fontSize: '14px', marginBottom: '8px' }}>
                  Device State: {this.state.deviceState}
                </div>
                <div style={{ color: tatchColors.black, fontSize: '14px' }}>
                  Error: {this.state.error}
                </div>
              </div>

              {/* Live Data */}
              <div style={{ marginBottom: '24px' }}>
                <SectionTitle>Live Data</SectionTitle>
                <div style={{ color: tatchColors.black, fontSize: '14px' }}>
                  {dataDisplay}
                </div>
              </div>

              {/* Command Sender */}
              <div style={{ marginBottom: '24px' }}>
                <SectionTitle>Command Sender</SectionTitle>
                <CommandSender onCommandSend={this.onCommandSend} response={this.state.commandResponse}/>
              </div>

              {/* Session Timer */}
              <div>
                <SectionTitle>Session Timer</SectionTitle>
                <SessionTimer startTime={this.state.sessionStartTime} isCounting={this.state.sessionIsActive}/>
              </div>
            </AdvancedSection>
          </Collapse>
        </div>
      </PatchCard>
    );
  }

  onCommandSend(cmd: Uint8Array): void {
    this.props.manager._sendCommand(cmd, true)
      .then((val: DataView | undefined ) => {
        if (val != undefined ) {
          this.setState({ commandResponse: buf2hex(val.buffer) });
        }
      });
  }

  onSpamCommand() {
    this.setStatus('Spamming commands...');
    this.props.manager.spamCommand()
      .then((_: any) => {
        this.setStatus('Commands sent...');
      });
  }

  onStartSession() {
    this.setStatus('Starting session...');
    this.props.manager.startSession((data) => {
      this.setState({
        spo2Data: data,
      });
    })
    	.catch(err => alert(err))
      .then((_: any) => {
        this.setState({
          sessionStartTime: new Date(),
          sessionIsActive: true,
        });
        this.setStatus('Session in progress...');
      });
  }

  onStopSession() {
    this.setStatus('Stopping session...');
    this.props.manager.stopSession()
      .then((_: any) => {
        this.setState({ sessionIsActive: false });
        this.setStatus('Idle');
      });
  }

  onGetPatchState() {
    this.props.manager.getDeviceState()
      .then((info: any) => {
        let str = '';
        if (info.success) {
          str += `${info.stateString} (${info.state}).`;
          str += ` ${info.remainingPages} pages unsent. Battery voltage: ${info.batteryVoltage / 1000}V.`;
          this.setState({
            deviceStateValue: info.stateString?.toLowerCase() || null,
            batteryVoltage: info.batteryVoltage / 1000, // Convert from mV to V
          });
        }
        else {
          str += 'GetDeviceState unsuccessful.';
          this.setState({
            deviceStateValue: null,
            batteryVoltage: null,
          });
        }
        str += ` rawMsg: (${info.rawMsg})`;
        this.setState({ deviceState: str });
      });
  }

  onTestBattery() {
    if (this.isBatteryTestBlocked()) {
      return;
    }

    const patchVersion = this.getPatchVersion(this.state.deviceInfo);

    if (patchVersion === 'Patch 1.0') {
      this.setState({
        isSamplingBattery: true,
        batteryTestComplete: false,
        batteryMode: 'pending',
        batterySoc: null,
      });
      this.completeVoltageBatteryTest();

      return;
    }

    this.runSocBatteryTest();
  }

  onDisconnectPatch() {
    this.setState({ sessionIsActive: false });
    this.props.manager.disconnect();
    this.setStatus('Disconnecting.');
    this.props.unmountHandler(this.props.manager.id);
  }

  onPowerOffPatch() {
    this.setState({ sessionIsActive: false });
    this.props.manager.powerOff();
    this.props.unmountHandler(this.props.manager.id);
  }

  onResetPatch() {
    this.setState({ sessionIsActive: false });
    this.setStatus('Resetting.');
    this.props.manager.reset();
  }

  onGetError() {
    this.props.manager.getError()
      .then((err: any) => {
        let str = '';
        if (err.isError) {
          const code = err.code.toString(16);
          str = `ERR 0x${code} raised at line ${err.lineNo} in "${err.fileName}" -- (${err.rawMsg})`;
        }
        else {
          str = 'None. Patch is not in error state.';
        }
        this.setState({ error: str });
      });
  }

  onFormatStorage() {
    this.setStatus('Clearing flash storage of session data...');
    this.props.manager.formatStorage()
      .then((_:any) => {
        this.setStatus('Storage cleared.');
      });
  }

  onRecoverCatchUp() {
    this.props.manager.startSessionRecovery((session: RecoverySession) => {
      if (session.error) this.setStatus(session.error);
      else {
        if (session.pagesReceived < session.pagesToRecover) {
          this.setStatus('Recovering page ' + session.pagesReceived + ' of ' +
          session.pagesToRecover + ' pages from device...');
        }
        else {
          this.setStatus('Recovery complete.');
        }
      }
    });
  }


  setStatus(msg: any) {
    this.setState({ status: msg });
  }

  onRecycle() {
    this.setState({ sessionIsActive: false, isRecycling: true });
    this.setStatus('Starting recycle process...');
    
    // Step 1: Format Storage
    this.setStatus('Formatting storage...');
    this.props.manager.formatStorage()
      .then((_: any) => {
        this.setStatus('Storage formatted. Resetting...');
        // Step 2: Reset

        return Promise.race([
          this.props.manager.reset(),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      })
      .then((_: any) => {
        this.setStatus('Reset complete. Powering off...');
        // Step 3: Power Off & Disconnect

        return new Promise((resolve) => setTimeout(resolve, 2000))
          .then(() => this.props.manager.powerOff());
      })
      .then((_: any) => {
        this.setStatus('Recycle complete.');
        this.props.unmountHandler(this.props.manager.id);
      })
      .catch((err: any) => {
        this.setState({ isRecycling: false });
        this.setStatus(`Recycle error: ${err.message || err}`);
        console.error('Recycle error:', err);
      });
  }

  onToggleAdvanced() {
    this.setState({ isAdvancedExpanded: !this.state.isAdvancedExpanded });
  }
}

interface ILiveSpO2Props {
  data: ITatchSpO2Data;
}


function LiveSpO2View(props: ILiveSpO2Props) {
  return (
    <div>Live data: {JSON.stringify(props.data)}</div>
  );
}


interface IDeviceInfoState {
  info: DeviceInfo | null
}

/**
 * Component for the display of the patch's device info
 *
 * @class      DeviceInfo (name)
 */
class DeviceInfoView extends React.Component<{patchManager: PatchManager}, IState> {
  state: IDeviceInfoState;

  constructor(props: any) {
    super(props);
    this.state = { info: null };
  }

  componentDidMount() {
    this.props.patchManager.getDeviceInfo()
      .then((info: DeviceInfo) => {
        console.log(info);
        this.setState({ info: info });
      });
  }

  render() {
    let contents = undefined;
    if (this.state.info === null) {
      contents = <span>"Awaiting device info..."</span>;
    }
    else {
      const infoTable = Object.entries(this.state.info).map((entry) => {
        return (
          <TableRow key={entry[0]}>
            <TableCell $isLabel style={{ textAlign: 'right', width: '200px' }}>{entry[0]}</TableCell>
            <TableCell style={{ textAlign: 'left' }}>{JSON.stringify(entry[1])}</TableCell>
          </TableRow>
        );
      });

      contents = <SummaryTable><tbody>{infoTable}</tbody></SummaryTable>;
    }

    return (
      <div style={
        {
          borderCollapse: 'collapse',
        }
      }>{contents}</div>
    );
  }
}

class SessionTimer extends React.Component<IProps, IState> {
  intervalID: any = null;
  constructor(props: any) {
    super(props);
    this.state = {
      durationMs: 0,
    };
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.isCounting !== this.props.isCounting) {
      // we've either started or stopped
      if (this.props.isCounting) {
        this.startTimer();
      }
      else {
        this.pauseTimer();
      }
    }
  }

  componentWillUnmount() {
    if (this.props.isCounting) {
      this.pauseTimer();
    }
  }

  render() {
    return (
      <div className='SessionTimer'>
        <div>Session Duration: {this.msToTime(this.state.durationMs)}</div>
        <div>Session Start Time: {this.props.startTime ? this.props.startTime.toString() : 'N/A'}</div>
      </div>
    );
  }

  startTimer() {
    this.intervalID = setInterval(_ => {
      const newDuration = this.props.startTime ? Math.abs(new Date().valueOf() - this.props.startTime.valueOf()) : 0;
      this.setState((prevState, props) => {
        return { durationMs: props.isCounting ? newDuration : prevState.durationMs };
      });
    }, 1000);
  }

  pauseTimer() {
    if (this.intervalID) {
      clearInterval(this.intervalID);
    }
  }

  /**
   * Converts milliseconds duration to hr:min:sec duration
   *
   * @param      {int}  durationMs  The duration in milliseconds
   */
  msToTime(durationMs: any) {
    let seconds: any = Math.floor((durationMs / 1000) % 60);
    let minutes: any = Math.floor((durationMs / (1000 * 60)) % 60);
    let hours: any = Math.floor((durationMs / (1000 * 60 * 60)) % 24);
    const milliseconds = parseInt(durationMs) % 1000 / 100;

    hours = (hours < 10) ? '0' + hours : hours;
    minutes = (minutes < 10) ? '0' + minutes : minutes;
    seconds = (seconds < 10) ? '0' + seconds : seconds;

    return hours + ':' + minutes + ':' + seconds + '.' + milliseconds;
  }
}


function CommandSender(props: {onCommandSend: (cmd: Uint8Array) => void, response: string}) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  function stringToArray(str: string): Uint8Array {
    const byteStrings = str.split(/\s+/);
    let array = new Uint8Array(byteStrings.length);
    for (let i = 0; i < byteStrings.length; i++) {
      array[i] = parseInt(byteStrings[i], 16);
    }

    return array;
  }

  const onSubmit = (data: any) => {
    let array = stringToArray(data.command);
    props.onCommandSend(array);
  };

  return (
    /* "handleSubmit" will validate your inputs before invoking "onSubmit" */
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* register your input into the hook by invoking the "register" function */}
      <span>Command: </span>
      <input defaultValue="08 00 10"
        {...register('command', {
          pattern: /^(?:[0-9a-fA-F]{2} *)+$/,
          required: true,
        })}
      />
      <input type="submit" />

      {/* errors will return when field validation fails  */}
      <div>
        {errors.command ? 'Command must be a sequence of valid hex bytes separated by spaces (i.e. 08 00 22)' : 'Valid command.'}
      </div>

      <div>
        Response: {props.response}
      </div>
    </form>
  );
}

/**
 * top-level component
 *
 * @class      App (name)
 */
class WesperMon extends React.Component<IProps, ITatchMonState> {
  constructor(props: any) {
    super(props);

    // list of patch BLE devices we've connected to.
    this.state = {
      devices: {},
    };

    this.onConnectButtonPress = this.onConnectButtonPress.bind(this);
    this.handlePatchInterfaceUnmount = this.handlePatchInterfaceUnmount.bind(this);
  }

  onConnectButtonPress(e: any) {
    console.log('Connecting...');
    connectToPatch()
      .then((newPatch: PatchManager) => {
        this.setState((state, props) => {
          const devices = {
            ...state.devices,
            [newPatch.id]: newPatch,
          };

          return { devices: devices };
        });
      });
  }

  handlePatchInterfaceUnmount(managerId: any) {
    this.setState((state) => {
      const devices = {
        ...state.devices,
      };
      delete devices[managerId];

      return { devices: devices };
    });
  }

  render() {
    return (
      <Container>
        <ConnectButton
          onClick={this.onConnectButtonPress}
          style={{ marginBottom: '24px' }}
        >
          Connect to Patch
        </ConnectButton>
        {
          Object.values(this.state.devices).map((device: PatchManager) => {
            return (<PatchInterface
              key={device.id} manager={device}
              unmountHandler={this.handlePatchInterfaceUnmount}/>);
          },
          )}
      </Container>
    );
  }
}

export default WesperMon;
